import clsx from 'clsx';

import NextLink from 'next/link';

import { ArrowRight, ArrowUpRight } from '@carbon/icons-react';
import type { LinkProps } from '@carbon/react';
import { Link as CarbonLink } from '@carbon/react';

import { getLinkProps, getUrlType } from '@/utils/links';

import styles from './link.module.scss';

export type ExtendedLinkProps = { showIcon?: boolean } & LinkProps<'a'>;

export function ExtendedLink(props: ExtendedLinkProps) {
  const { href, className, showIcon = false, inline = false, size = 'lg', ...rest } = props;
  const urlType = getUrlType(href);
  const isExternalUrl = urlType === 'corporate-external' || urlType === 'external';

  if (!href) {
    return <span>{props.children}</span>;
  }

  if (urlType === 'internal') {
    return (
      <NextLink
        {...rest}
        href={href}
        className={clsx(
          styles['link'],
          styles[`link--${size}`],
          { [styles['link--inline']]: inline },
          'text-link',
          className
        )}
      >
        {props.children}
        {showIcon && !inline && (
          <div className={styles['link__arrow']}>
            <ArrowRight width={16} />
          </div>
        )}
      </NextLink>
    );
  }

  if (isExternalUrl) {
    return (
      <CarbonLink
        {...rest}
        inline={inline}
        href={href}
        className={className}
        size={size}
        {...getLinkProps(href)}
      >
        {props.children} <span className="sr-only">(opens in a new tab)</span>
        {showIcon && !inline && (
          <div className={styles['link__arrow']}>
            <ArrowUpRight width={16} />
          </div>
        )}
      </CarbonLink>
    );
  }

  return <CarbonLink {...rest} href={href} className={className} inline={inline} size={size} />;
}
